import React, { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Calendar, Edit as EditIcon } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import {
  TextArea,
  Timepicker,
  TimepickerInputLabel,
  TimepickerInputRoot,
} from '@mint-lib/tw-components/patterns';
import { format } from 'date-fns';

import { Select } from '../../../components/Select/Select.jsx';
import { schedulingOptions } from '../../../constants/scheduling.js';
import { useToggleScheduleDialog } from '../../../hooks/useToggleScheduleDialog.jsx';
import {
  useCreateScheduleMutation,
  useEditScheduleMutation,
} from '../../../queries/schedule.js';
import { RangeDatepicker } from './RangeDatepicker.jsx';
import { useDialogValues } from './useDialogValues.jsx';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const modifyDate = (date: Date, time: Date) => {
  const mergedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds(),
  );

  return format(mergedDate, "yyyy-MM-dd'T'HH:mm:ss");
};

const CreateScheduleDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { setEditQuery, editQuery } = useToggleScheduleDialog();
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const { fields, updateField } = useDialogValues();

  const { mutateAsync: createScheduleM, isPending } =
    useCreateScheduleMutation();
  const { mutateAsync: editScheduleM, isPending: isPendingEdit } =
    useEditScheduleMutation();

  const isEditMode = Boolean(editQuery);

  const isDisabledSaveBtn = useMemo(() => {
    if (isEditMode) {
      const { chatName, ...restFields } = fields;
      return Object.values(restFields).some((f) => !f);
    }

    return Object.values(fields).some((f) => !f);
  }, [fields, isEditMode]);

  const handleSaveSchedule = async () => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!fields.scheduling || !fields.from || !fields.to || !fields.time) {
      return;
    }

    const formattedStartDate = modifyDate(fields.from, fields.time);
    const formattedEndDate = modifyDate(fields.to, fields.time);

    try {
      if (editQuery) {
        const { query: _query, ...editQueryRest } = editQuery;
        await editScheduleM({
          ...editQueryRest,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          scheduling: fields.scheduling,
          chatTitle: fields.chatName,
          ...(editQuery.status === 'scheduled' && { query: fields.query }),
        });

        setEditQuery(null);

        enqueueSnackbar({
          variant: 'success',
          title: t('Schedule successfully updated'),
        });
      } else {
        await createScheduleM({
          scheduling: fields.scheduling,
          chatTitle: fields.chatName,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          userTimezone,
          query: fields.query,
        });

        enqueueSnackbar({
          variant: 'success',
          title: t('Chat successfully scheduled'),
        });
      }

      onClose();
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: t('Error'),
        subTitle: t('Failed to schedule chat'),
        textLayout: 'inline',
      });
    }
  };

  const handleCloseSchedule = () => {
    setEditQuery(null);
    onClose();
  };

  return (
    <Dialog modal open={isOpen} onOpenChange={handleCloseSchedule}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          className="max-h-screen"
          aria-describedby={undefined}
          size="m"
        >
          <DialogHeader onClose={handleCloseSchedule}>
            <DialogTitle className="flex flex-row items-center gap-2">
              {isEditMode ? (
                <EditIcon className="size-4 fill-icon-primary" />
              ) : (
                <Calendar className="size-4 fill-icon-primary" />
              )}
              <p>{isEditMode ? t('Edit') : t('Schedule')}</p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex flex-col gap-4 overflow-auto">
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-2">
                <Select
                  infoText={t('Choose how often you want this query to run.')}
                  label={t('Scheduling')}
                  initialValue={fields.scheduling}
                  options={schedulingOptions}
                  placeholder={t('Select recurrence type...')}
                  onChange={(v) => {
                    if (v) {
                      updateField('scheduling', v as typeof fields.scheduling);
                    }
                  }}
                />
              </div>
              <div className="col-span-2">
                <RangeDatepicker
                  label={t('Start and end date')}
                  infoText={t(
                    'Set the date when the scheduled query should begin and stop running.',
                  )}
                  startDate={fields.from}
                  endDate={fields.to}
                  onSelectCalendarDates={(dates) => {
                    updateField('from', dates?.from);
                    updateField('to', dates?.to);
                  }}
                  onResetCalendar={() => {
                    updateField('from', undefined);
                    updateField('to', undefined);
                  }}
                />
              </div>
              <TimepickerInputRoot className="col-span-1" ref={setContainerRef}>
                <TimepickerInputLabel
                  htmlFor="schedule-time-picker"
                  label="Time"
                  infoIconWithTooltip={t(
                    'Set the time  when the scheduled query should begin running',
                  )}
                  container={containerRef}
                />
                <Timepicker
                  id="schedule-time-picker"
                  className="w-full"
                  popoverClassName="z-mui"
                  onChange={(date) => {
                    if (date instanceof Date) {
                      updateField('time', date);
                    }
                  }}
                  value={fields.time}
                  placeholder="00:00"
                  container={containerRef}
                />
              </TimepickerInputRoot>
            </div>
            <TextArea
              data-testid="chat-name"
              label={t('Chat name')}
              placeholder={t('Enter chat name...')}
              onChange={(e) => {
                const { value } = e.target;
                updateField('chatName', value);
              }}
              value={fields.chatName}
              maxLength={200}
            />
            <TextArea
              data-testid="scheduled-query"
              label={t('Scheduled query')}
              placeholder={t('Enter query...')}
              onChange={(e) => {
                const { value } = e.target;
                updateField('query', value);
              }}
              value={fields.query}
              rows={10}
              maxLength={2000}
              disabled={isEditMode && editQuery?.status !== 'scheduled'}
            />
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="plain" onClick={handleCloseSchedule}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              disabled={
                isDisabledSaveBtn || (isEditMode ? isPendingEdit : isPending)
              }
              variant="contained"
              onClick={handleSaveSchedule}
            >
              {(isPendingEdit || isPending) && (
                <CircularLoadingButton size="small" variant="contained" />
              )}
              {isEditMode ? t('Save changes') : t('Save')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { CreateScheduleDialog };
